import { CurrentItemPrice } from "Components/Merchant/Initiate/InitiatePaymentKeypad/CurrentItemPrice";
import { InitiatePaymentButton } from "Components/Merchant/Initiate/InitiatePaymentKeypad/InitiatePaymentButton";
import { Keypad } from "Components/Merchant/Initiate/InitiatePaymentKeypad/Keypad/Keypad";
import { PaypointDropdown } from "Components/Merchant/Initiate/InitiatePaymentKeypad/PaypointDropdown";
import { PriceBreakdownList } from "Components/Merchant/Initiate/InitiatePaymentKeypad/PriceBreakdownList";
import { UnstyledLink } from "Components/Routes/UnstyledLink";
import { useUserMarketCurrency } from "Hooks/useUserMarketCurrency";
import { PayPointMode, type PayPointBaseDto } from "Infrastructure/Api/Api";
import { Resources, useResource } from "Translations/Resources";
import { appUrl } from "Utils/UrlUtils";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import BackspaceOutlinedIcon from "@mui/icons-material/BackspaceOutlined";
import SensorsIcon from "@mui/icons-material/Sensors";
import SensorsOffIcon from "@mui/icons-material/SensorsOff";
import { Box, Stack, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { ErrorText } from "Components/Shared/Errors/ErrorText";

import { LocalStorageKeys } from "Utils/Storage/LocalStorage/LocalStorageKeys";
import {
  getLocalStorageItem,
  setLocalStorageItem,
  removeLocalStorageItem,
} from "Utils/Storage/LocalStorage/SecureLocalStorage";

type Props = {
  onPay: (amount: number, paypointID?: string) => void;
  bankAccountID?: number;
  isDisabled?: boolean;
  paypoints?: Array<PayPointBaseDto>;
  isUsedByEmployee?: boolean;
};

const isEnabledForKeypadPayments = ({ mode }: PayPointBaseDto) =>
  mode === PayPointMode.Standard;

const getDefaultKeypadPaypoint = (
  paypoints: Array<PayPointBaseDto>,
  isUsedByEmployee: boolean,
): PayPointBaseDto | null => {
  if (paypoints.length === 0) {
    return null;
  }
  const paypointPublicIdFromLocalStorage = getLocalStorageItem(
    LocalStorageKeys.SelectedPaypoint,
  );
  const storedPaypoint = paypointPublicIdFromLocalStorage
    ? paypoints.find((p) => p.publicID === paypointPublicIdFromLocalStorage)
    : null;

  return (
    storedPaypoint ||
    (isUsedByEmployee
      ? paypoints.find((p) => p.hasConnectedAccount) || paypoints[0]
      : null)
  );
};

export const InitiatePaymentKeypad = ({
  onPay,
  bankAccountID,
  isDisabled,
  paypoints,
  isUsedByEmployee,
}: Props) => {
  const keypadPaypoints = (paypoints ?? []).filter(isEnabledForKeypadPayments);

  const [selectedPaypoint, setSelectedPaypoint] =
    useState<PayPointBaseDto | null>(() =>
      getDefaultKeypadPaypoint(keypadPaypoints, !!isUsedByEmployee),
    );
  const [priceInCents, setPriceInCents] = useState(0);
  const [previousPricesInCents, setPreviousPricesInCents] = useState<
    Array<number>
  >([]);
  const { t } = useResource();
  // Same source as the payment payload (useAmount reads the same region), so the
  // displayed currency can never diverge from the one the transaction is created in.
  const currencyCode = useUserMarketCurrency();

  useEffect(() => {
    if (selectedPaypoint?.publicID) {
      setLocalStorageItem(
        LocalStorageKeys.SelectedPaypoint,
        selectedPaypoint.publicID,
      );
    } else {
      removeLocalStorageItem(LocalStorageKeys.SelectedPaypoint);
    }
  }, [selectedPaypoint?.publicID]);

  const handleNumberPress = (digit: number) => {
    setPriceInCents((previousPrice) => previousPrice * 10 + digit);
  };

  const handleReset = () => {
    setPriceInCents(0);
    setPreviousPricesInCents([]);
  };

  const handleAdd = () => {
    if (priceInCents !== 0) {
      setPreviousPricesInCents((prev) => [...prev, priceInCents]);
      setPriceInCents(0);
    }
  };

  const handleBackspace = () => {
    if (priceInCents > 0) {
      setPriceInCents((previousPrice) => Math.floor(previousPrice / 10));
    }
  };

  const totalInCents =
    priceInCents + previousPricesInCents.reduce((item, acc) => acc + item, 0);

  // biome-ignore format: let's keep one cell per row
  const keypadGrid = [
    [
      { label: "1", onClick: () => handleNumberPress(1) },
      { label: "2", onClick: () => handleNumberPress(2) },
      { label: "3", onClick: () => handleNumberPress(3) },
    ],
    [
      { label: "4", onClick: () => handleNumberPress(4) },
      { label: "5", onClick: () => handleNumberPress(5) },
      { label: "6", onClick: () => handleNumberPress(6) },
    ],
    [
      { label: "7", onClick: () => handleNumberPress(7) },
      { label: "8", onClick: () => handleNumberPress(8) },
      { label: "9", onClick: () => handleNumberPress(9) },
    ],
    [
      { label: "C", onClick: handleReset },
      { label: "0", onClick: () => handleNumberPress(0) },
      { label: "00", onClick: () => { handleNumberPress(0); handleNumberPress(0); } },
    ],
    [
      { label: "" }, // empty cell
      { label: <BackspaceOutlinedIcon />, onClick: handleBackspace },
      { label: <AddOutlinedIcon color="primary" />, onClick: handleAdd },
    ],
  ];

  const getOnPaypointPayHandler = (paypointID: string) => (price: number) =>
    onPay(price, paypointID);

  const isPaymentDisabled =
    isDisabled ||
    totalInCents <= 0 ||
    (selectedPaypoint && !selectedPaypoint.hasConnectedAccount) ||
    (!isUsedByEmployee && !bankAccountID) ||
    (isUsedByEmployee && !selectedPaypoint?.hasConnectedAccount);

  return (
    <Stack gap={4} justifyContent="space-between">
      <Stack sx={{ lineBreak: "anywhere" }} mt={3} gap={1}>
        <CurrentItemPrice
          priceInCents={priceInCents}
          currencyCode={currencyCode}
        />
        <PriceBreakdownList
          previousPricesInCents={previousPricesInCents}
          currentPriceInCents={priceInCents}
          currencyCode={currencyCode}
        />
      </Stack>
      <Stack gap={4}>
        <Keypad grid={keypadGrid} />
        <Box display="flex" alignItems="center" width="100%" mx={2}>
          {selectedPaypoint?.nfcCardNo ? (
            <SensorsIcon sx={{ flexShrink: 1 }} aria-hidden="true" />
          ) : (
            <SensorsOffIcon sx={{ flexShrink: 1 }} aria-hidden="true" />
          )}
          <Box flexGrow={1} width="100%">
            <PaypointDropdown
              isNoPaypointChoiceExcluded={isUsedByEmployee}
              paypoints={keypadPaypoints}
              selectedPaypoint={selectedPaypoint}
              setSelectedPaypoint={setSelectedPaypoint}
            />
          </Box>
        </Box>
        {selectedPaypoint && !selectedPaypoint.hasConnectedAccount && (
          <ErrorText display="flex" gap={1} flexWrap="wrap">
            {t(Resources.Merchant.Initiate.PaypointHasNoAccountError)}
          </ErrorText>
        )}
        <InitiatePaymentButton
          onPay={
            selectedPaypoint
              ? getOnPaypointPayHandler(selectedPaypoint.publicID)
              : onPay
          }
          priceInCents={totalInCents}
          currencyCode={currencyCode}
          isDisabled={isPaymentDisabled}
        />
        {!isUsedByEmployee && !bankAccountID && (
          <Typography display="flex" gap={1} flexWrap="wrap">
            {t(Resources.Merchant.Initiate.ConnectBankAccountLink.PreText)}
            <UnstyledLink to={appUrl("bank-address-list")}>
              {t(Resources.Merchant.Initiate.ConnectBankAccountLink.LinkText)}
            </UnstyledLink>
          </Typography>
        )}
      </Stack>
    </Stack>
  );
};
